/**
 * Dynamic Yield wishlist heart injection (theme-owned)
 *
 * Injects `<wishlist-button class="wishlist-button--dy" data-context="dy">` into
 * `.wishlist-dy-icon-wrapper` placeholders when data-variant-id and data-product-id
 * are present (SKU optional — same as legacy app extension).
 *
 * Uses a debounced document.body MutationObserver (legacy parity) plus optional
 * scoped observers on known DY roots for faster first paint.
 */
(() => {
  'use strict';

  const WRAPPER_SELECTOR = '.wishlist-dy-icon-wrapper';
  const INJECTED_ATTR = 'data-wishlist-dy-injected';
  const BODY_DEBOUNCE_MS = 200;
  const ROOT_STABILITY_MS = 500;
  const IDLE_FALLBACK_MS = 2000;
  const HEART_PATH =
    'M5.1582 1.79517C6.17372 1.79065 7.28182 2.15343 8.1748 3.08618L9.00098 3.94458L9.82812 3.09302L9.98633 2.93677C11.6532 1.37056 14.2473 1.4228 15.8525 3.09302C17.5074 4.81504 17.5074 7.57902 15.8525 9.30103L9.37207 16.053L9.37109 16.052C9.27211 16.1558 9.13306 16.2053 8.99805 16.2053C8.86286 16.2053 8.72303 16.1561 8.62402 16.052L2.14258 9.29419C0.489871 7.57079 0.49474 4.80792 2.14941 3.08618C2.97743 2.22457 4.06493 1.79529 5.1582 1.79517Z';

  /** @type {Map<Element, MutationObserver>} */
  const rootObservers = new Map();
  /** @type {Map<Element, number>} */
  const rootDebounceTimers = new Map();
  /** @type {Set<Element>} */
  const completedRoots = new Set();

  let bodyDebounceTimer = null;
  /** @type {MutationObserver | null} */
  let bodyObserver = null;
  /** @type {Promise<void>} */
  let injectChain = Promise.resolve();

  const getWishlist = () => {
    const win = /** @type {any} */ (window);
    return win.theme && win.theme.wishlist ? win.theme.wishlist : null;
  };

  /**
   * @param {Element} wrapper
   */
  const getWrapperAttrs = (wrapper) => {
    const productId =
      wrapper.getAttribute('data-product-id') ||
      wrapper.getAttribute('data-productid') ||
      wrapper.dataset.productId;
    const variantId =
      wrapper.getAttribute('data-variant-id') ||
      wrapper.getAttribute('data-variantid') ||
      wrapper.dataset.variantId;
    const sku =
      wrapper.getAttribute('data-variant-sku') ||
      wrapper.getAttribute('data-sku') ||
      wrapper.getAttribute('data-variantsku') ||
      wrapper.dataset.variantSku ||
      wrapper.dataset.sku ||
      '';
    return { productId, variantId, sku: sku ? String(sku).trim() : '' };
  };

  /**
   * @param {string} productId
   * @param {string} variantId
   * @param {string} sku
   */
  const buildButtonHtml = (productId, variantId, sku) => {
    const skuAttr = sku
      ? ' data-sku="' + String(sku).replace(/"/g, '&quot;') + '"'
      : ' data-sku=""';

    return (
      '<wishlist-button class="wishlist-button wishlist-button--dy hidden" ' +
      'data-context="dy" ' +
      'data-product-id="' +
      String(productId) +
      '" ' +
      'data-variant-id="' +
      String(variantId) +
      '"' +
      skuAttr +
      '>' +
      '<button type="button" class="wishlist-button__btn" aria-pressed="false" aria-label="Add to wishlist">' +
      '<svg class="wishlist-button__icon wishlist-button__icon--empty" width="22" height="22" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      '<path d="' +
      HEART_PATH +
      '"/>' +
      '</svg>' +
      '<svg class="wishlist-button__icon wishlist-button__icon--filled" width="22" height="22" viewBox="0 0 18 18" fill="currentColor" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      '<path d="' +
      HEART_PATH +
      '"/>' +
      '</svg>' +
      '</button>' +
      '</wishlist-button>'
    );
  };

  const IMAGE_HOST_SELECTORS = [
    '.dy-recommendation-item__image-wrapper',
    '.card-gallery-default',
    '[class*="image-wrapper"]',
    '[class*="image-container"]',
    '[class*="product-image"]',
    '[class*="img-wrapper"]',
  ];

  /**
   * Anchor the DY placeholder over the product image so top-right CSS matches PLP cards.
   * @param {Element} wrapper
   */
  const positionDyWrapper = (wrapper) => {
    if (!(wrapper instanceof Element)) return;

    /** @type {Element | null} */
    let imageHost = null;

    for (const selector of IMAGE_HOST_SELECTORS) {
      const match = wrapper.closest(selector);
      if (match instanceof Element) {
        imageHost = match;
        break;
      }
    }

    if (!imageHost) {
      let parent = wrapper.parentElement;
      while (parent && parent !== document.body) {
        if (parent.querySelector('img')) {
          imageHost = parent;
          break;
        }
        parent = parent.parentElement;
      }
    }

    if (!imageHost) return;

    if (window.getComputedStyle(imageHost).position === 'static') {
      imageHost.classList.add('wishlist-dy-image-host');
    }

    if (wrapper.parentElement !== imageHost) {
      imageHost.appendChild(wrapper);
    }
  };

  /**
   * @param {Element} wrapper
   */
  const finalizeDyWrapper = (wrapper) => {
    positionDyWrapper(wrapper);
    wrapper.setAttribute('aria-hidden', 'false');
  };

  /**
   * @param {Element} wrapper
   */
  const dedupeButtonsInWrapper = (wrapper) => {
    const buttons = wrapper.querySelectorAll('wishlist-button');
    for (let i = 1; i < buttons.length; i++) {
      buttons[i].remove();
    }
  };

  /**
   * @param {ParentNode} scope
   * @returns {boolean}
   */
  const injectInScope = (scope) => {
    const wrappers = scope.querySelectorAll
      ? scope.querySelectorAll(WRAPPER_SELECTOR)
      : [];
    let injectedAny = false;

    wrappers.forEach((wrapper) => {
      if (!(wrapper instanceof Element)) return;

      dedupeButtonsInWrapper(wrapper);

      if (wrapper.querySelector('wishlist-button')) {
        wrapper.setAttribute(INJECTED_ATTR, 'true');
        finalizeDyWrapper(wrapper);
        return;
      }

      // Stale marker from a prior failed/partial inject — allow retry.
      if (wrapper.getAttribute(INJECTED_ATTR) === 'true') {
        wrapper.removeAttribute(INJECTED_ATTR);
      }

      if (wrapper.getAttribute(INJECTED_ATTR) === 'pending') return;

      const { productId, variantId, sku } = getWrapperAttrs(wrapper);
      if (!productId || !variantId) return;

      wrapper.insertAdjacentHTML('beforeend', buildButtonHtml(productId, variantId, sku));
      wrapper.setAttribute(INJECTED_ATTR, 'true');
      finalizeDyWrapper(wrapper);
      injectedAny = true;
    });

    return injectedAny;
  };

  const syncDyWishlist = () => {
    const wishlist = getWishlist();
    if (!wishlist || typeof wishlist.sync !== 'function') return;

    // Sync every DY heart, then full document as safety net (handles wishlist:ready race).
    document.querySelectorAll(`${WRAPPER_SELECTOR} wishlist-button`).forEach((button) => {
      wishlist.sync(button);
    });
    wishlist.sync(document);
  };

  const runInjectWork = () => {
    const injectedAny = injectInScope(document);
    if (injectedAny) syncDyWishlist();
    return injectedAny;
  };

  const scheduleGlobalInject = () => {
    injectChain = injectChain.then(() => {
      return new Promise((resolve) => {
        const run = () => resolve(runInjectWork());
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(() => run(), { timeout: 500 });
        } else {
          run();
        }
      });
    });
    return injectChain;
  };

  const scheduleBodyInject = () => {
    if (bodyDebounceTimer) window.clearTimeout(bodyDebounceTimer);
    bodyDebounceTimer = window.setTimeout(() => {
      bodyDebounceTimer = null;
      scheduleGlobalInject();
    }, BODY_DEBOUNCE_MS);
  };

  /**
   * @param {Element} root
   */
  const scheduleRootInject = (root) => {
    const existing = rootDebounceTimers.get(root);
    if (existing) window.clearTimeout(existing);

    const timer = window.setTimeout(() => {
      rootDebounceTimers.delete(root);
      scheduleGlobalInject().then(() => {
        const pending = root.querySelectorAll(WRAPPER_SELECTOR);
        let allDone = true;
        pending.forEach((wrapper) => {
          if (wrapper.querySelector('wishlist-button')) return;
          const attrs = getWrapperAttrs(wrapper);
          if (attrs.productId && attrs.variantId) allDone = false;
        });
        if (allDone && rootObservers.has(root)) {
          rootObservers.get(root)?.disconnect();
          rootObservers.delete(root);
          completedRoots.add(root);
        }
      });
    }, ROOT_STABILITY_MS);

    rootDebounceTimers.set(root, timer);
  };

  /**
   * @param {Element} root
   */
  const attachRootObserver = (root) => {
    if (completedRoots.has(root) || rootObservers.has(root)) return;
    if (!window.MutationObserver) {
      scheduleGlobalInject();
      return;
    }

    const observer = new MutationObserver(() => scheduleRootInject(root));
    observer.observe(root, { childList: true, subtree: true });
    rootObservers.set(root, observer);
    scheduleRootInject(root);
  };

  const findDyRoots = () => {
    /** @type {Element[]} */
    const roots = [];
    const seen = new Set();

    const addRoot = (el) => {
      if (!(el instanceof Element) || seen.has(el)) return;
      seen.add(el);
      roots.push(el);
    };

    document.querySelectorAll('[data-dy-reco-section]').forEach(addRoot);
    document.querySelectorAll('[id^="spy-"]').forEach(addRoot);
    document.querySelectorAll('[class*="spy-"]').forEach((el) => {
      if (el instanceof Element) addRoot(el);
    });

    return roots;
  };

  const attachBodyObserver = () => {
    if (bodyObserver || !window.MutationObserver || !document.body) return;

    bodyObserver = new MutationObserver(() => scheduleBodyInject());
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  };

  const bootstrap = () => {
    findDyRoots().forEach(attachRootObserver);
    attachBodyObserver();
    scheduleGlobalInject();

    window.setTimeout(() => {
      scheduleGlobalInject().then(syncDyWishlist);
    }, 600);

    window.setTimeout(() => {
      scheduleGlobalInject().then(syncDyWishlist);
    }, IDLE_FALLBACK_MS);
  };

  const onWishlistReady = () => {
    scheduleGlobalInject().then(syncDyWishlist);
  };

  const resyncDyRoots = () => {
    document.querySelectorAll(WRAPPER_SELECTOR).forEach((wrapper) => {
      if (wrapper instanceof Element && wrapper.querySelector('wishlist-button')) {
        positionDyWrapper(wrapper);
      }
    });
    syncDyWishlist();
  };

  document.addEventListener('wishlist:ready', onWishlistReady);
  document.addEventListener('wishlist:updated', resyncDyRoots);
  document.addEventListener('cart:synced', resyncDyRoots);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  /** @type {any} */ (window).WishlistDyInject = {
    bootstrap,
    injectInRoot: (root) => {
      const injectedAny = injectInScope(root || document);
      if (injectedAny) syncDyWishlist();
      return injectedAny;
    },
    inject: scheduleGlobalInject,
    sync: syncDyWishlist,
  };
})();
